import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Service,
  Venue,
  AddOn,
  Appointment,
  Company,
  CompanyUser,
  Customer,
  CheckoutPricing,
  BookingAvailabilitySlot
} from '@/types';
import { X, AlertCircle, ChevronLeft, CheckCircle, Loader2, CreditCard } from 'lucide-react';
import { getDefaultCountryCode } from '@/constants';
import { api } from '@/services/api';
import { tokenizeCardDetails } from '@/services/opayo';
import { filterPastAvailabilitySlots } from '@/lib/scheduling';
import { formatDateOnly } from '@/lib/dates';
import { formatDobFromIso, normalizeDobToIso } from '@/lib/formatters';
import { buildMapById } from '@/lib/collections';
import { validateUkPhone, validateEmail, validateDob } from '@/lib/validators';
import { buildBrowserInfo } from '@/lib/opayo';
import { getTierPrice } from '@/lib/pricing';
import { normalizeErrorMessage } from '@/lib/errorUtils';
import { BookingScheduleStep } from './BookingScheduleStep';
import { BookingPatientStep } from './BookingPatientStep';
import {
  CUSTOMER_SEARCH_PAGE_SIZE,
  MAX_CUSTOMER_SEARCH_PAGES,
  normalizeSearchText,
  getCustomerLookupKey,
  buildBookedByOptions
} from './manualBookingUtils';

interface ManualBookingModalProps {
  onClose: () => void;
  onSave: (booking: Appointment) => Promise<void> | void;
  services: Service[];
  venues: Venue[];
  addOns: AddOn[];
  servicesById?: Map<string, Service>;
  venuesById?: Map<string, Venue>;
  addOnsById?: Map<string, AddOn>;
  customers: Customer[];
  companies: Company[];
  appointments: Appointment[];
}

type ThreeDSData = {
  acsUrl: string;
  cReq: string;
  termUrl: string;
  threeDSSessionData?: string;
};

export const ManualBookingModal: React.FC<ManualBookingModalProps> = ({
  onClose,
  onSave,
  services,
  venues,
  addOns,
  servicesById: servicesByIdFromProps,
  venuesById: venuesByIdFromProps,
  addOnsById: addOnsByIdFromProps,
  customers,
  companies,
  appointments
}) => {
  const currentYear = new Date().getFullYear();
  const [threeDSData, setThreeDSData] = useState<ThreeDSData | null>(null);
  const [step, setStep] = useState(1);
  const [isCorporate, setIsCorporate] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'invoice'>('card');
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [companySearch, setCompanySearch] = useState('');
  const [companySearchResults, setCompanySearchResults] = useState<Company[]>([]);
  const [isCompanySearchLoading, setIsCompanySearchLoading] = useState(false);
  const [companySearchError, setCompanySearchError] = useState('');
  const [isCompanyDropdownOpen, setIsCompanyDropdownOpen] = useState(false);
  const [companyTeam, setCompanyTeam] = useState<CompanyUser[]>([]);
  const [isCompanyTeamLoading, setIsCompanyTeamLoading] = useState(false);
  const [bookedByCompanyUserId, setBookedByCompanyUserId] = useState('primary');
  const [poNumber, setPoNumber] = useState('');
  const [allowOverbook, setAllowOverbook] = useState(false);
  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [checkoutPricing, setCheckoutPricing] = useState<CheckoutPricing | null>(null);
  const [pendingPaymentId, setPendingPaymentId] = useState<string | undefined>(undefined);
  const [pendingPaymentLinkToken, setPendingPaymentLinkToken] = useState<string | undefined>(
    undefined
  );
  const [availabilitySlots, setAvailabilitySlots] = useState<BookingAvailabilitySlot[]>([]);
  const [hasAvailabilityData, setHasAvailabilityData] = useState(false);
  const [isLoadingAvailability, setIsLoadingAvailability] = useState(false);
  const [paymentResultModal, setPaymentResultModal] = useState<{
    type: 'success' | 'error';
    title?: string;
    message: string;
  } | null>(null);

  const [cardDetails, setCardDetails] = useState({
    number: '',
    expiry: '',
    cvc: '',
    cardholderName: ''
  });
  const [billingAddress, setBillingAddress] = useState({
    address1: '',
    city: '',
    postalCode: '',
    country: getDefaultCountryCode()
  });

  const [customerSearch, setCustomerSearch] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState<Customer[]>([]);
  const [isCustomerSearchLoading, setIsCustomerSearchLoading] = useState(false);
  const [customerSearchError, setCustomerSearchError] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [customerDetails, setCustomerDetails] = useState({
    name: '',
    email: '',
    phone: '',
    dob: '',
    jobTitle: ''
  });

  const [dobTouched, setDobTouched] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);

  const [bookingData, setBookingData] = useState({
    venueId: '',
    serviceIds: [] as string[],
    addOnIds: [] as string[],
    date: null as Date | null,
    time: ''
  });

  const servicesById = useMemo(
    () => servicesByIdFromProps ?? buildMapById(services),
    [servicesByIdFromProps, services]
  );
  const addOnsById = useMemo(
    () => addOnsByIdFromProps ?? buildMapById(addOns),
    [addOnsByIdFromProps, addOns]
  );
  const venuesById = useMemo(
    () => venuesByIdFromProps ?? buildMapById(venues),
    [venuesByIdFromProps, venues]
  );

  const [visibleMonth, setVisibleMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const selectedCompany =
    companySearchResults.find((c) => c.id === selectedCompanyId) ||
    companies.find((c) => c.id === selectedCompanyId);

  const normalizedCustomerSearch = normalizeSearchText(customerSearch);
  const hasCustomerSearch = Boolean(normalizedCustomerSearch);
  const localCustomerMatches = hasCustomerSearch
    ? customers.filter((customer) => {
        const name = normalizeSearchText(customer.name);
        const email = normalizeSearchText(customer.email);
        return name.includes(normalizedCustomerSearch) || email.includes(normalizedCustomerSearch);
      })
    : [];
  const filteredCustomers = hasCustomerSearch
    ? (() => {
        const merged = new Map<string, Customer>();
        localCustomerMatches.forEach((c) => merged.set(getCustomerLookupKey(c), c));
        customerSearchResults.forEach((c) => merged.set(getCustomerLookupKey(c), c));
        return Array.from(merged.values())
          .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
          .slice(0, 10);
      })()
    : [];

  const eligibleCompanies = [...companies]
    .filter((c) => c.status === 'active' || c.status === 'on_hold')
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  const normalizedCompanySearch = companySearch.trim().toLowerCase();
  const hasCompanySearch = Boolean(normalizedCompanySearch);
  const localCompanyMatches = hasCompanySearch
    ? eligibleCompanies.filter(
        (c) =>
          c.name.toLowerCase().includes(normalizedCompanySearch) ||
          c.email.toLowerCase().includes(normalizedCompanySearch)
      )
    : [];
  const filteredCompanies = hasCompanySearch
    ? (() => {
        const merged = new Map<string, Company>();
        localCompanyMatches.forEach((c) => merged.set(c.id, c));
        companySearchResults.forEach((c) => merged.set(c.id, c));
        return Array.from(merged.values()).sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
        );
      })()
    : (() => {
        if (!selectedCompany) return eligibleCompanies;
        if (eligibleCompanies.some((c) => c.id === selectedCompany.id)) return eligibleCompanies;
        return [...eligibleCompanies, selectedCompany].sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
        );
      })();

  const handleSelectExistingCustomer = useCallback((c: Customer) => {
    setSelectedCustomerId(c.id);
    setCustomerDetails({
      name: c.name,
      email: c.email,
      phone: c.phone || '',
      dob: formatDobFromIso(c.dob || ''),
      jobTitle: c.jobTitle || ''
    });
    setCustomerSearch('');
  }, []);

  const handleCreateNewCustomer = () => {
    setSelectedCustomerId(null);
    setCustomerDetails({ name: '', email: '', phone: '', dob: '', jobTitle: '' });
  };

  useEffect(() => {
    const term = customerSearch.trim();
    if (!term || selectedCustomerId) {
      setCustomerSearchResults([]);
      setCustomerSearchError('');
      setIsCustomerSearchLoading(false);
      return;
    }

    let isActive = true;
    setIsCustomerSearchLoading(true);
    setCustomerSearchError('');
    setCustomerSearchResults([]);

    const timeoutId = window.setTimeout(() => {
      if (!isActive) return;
      const load = async () => {
        try {
          const merged = new Map<string, Customer>();
          let page = 1;
          let totalPages = 1;
          while (isActive && page <= totalPages && page <= MAX_CUSTOMER_SEARCH_PAGES) {
            const data = await api.listStaffCustomers({
              page,
              pageSize: CUSTOMER_SEARCH_PAGE_SIZE,
              q: term,
              sortBy: 'name',
              sortDir: 'asc'
            });
            if (!isActive) return;
            const items = data.items || [];
            totalPages = Math.max(
              1,
              Math.ceil(Number(data.totalCount || 0) / CUSTOMER_SEARCH_PAGE_SIZE)
            );
            if (!items.length) break;
            items.forEach((c) => merged.set(getCustomerLookupKey(c), c));
            page += 1;
          }
          if (!isActive) return;
          setCustomerSearchResults(
            Array.from(merged.values()).sort((a, b) =>
              a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
            )
          );
        } catch (err: unknown) {
          if (!isActive) return;
          setCustomerSearchResults([]);
          setCustomerSearchError(normalizeErrorMessage(err, 'Failed to search patients.'));
        } finally {
          if (isActive) setIsCustomerSearchLoading(false);
        }
      };
      load();
    }, 250);

    return () => {
      isActive = false;
      window.clearTimeout(timeoutId);
    };
  }, [customerSearch, selectedCustomerId]);

  useEffect(() => {
    if (selectedCustomerId) return;
    const normalizedEmail = normalizeSearchText(customerDetails.email);
    if (!normalizedEmail) return;

    const knownCustomers = [...customers, ...customerSearchResults];
    const matched = knownCustomers.find((c) => normalizeSearchText(c.email) === normalizedEmail);
    if (matched) {
      handleSelectExistingCustomer(matched);
      return;
    }
    if (!normalizedEmail.includes('@')) return;

    let isActive = true;
    const lookup = async () => {
      try {
        const data = await api.listStaffCustomers({
          page: 1,
          pageSize: CUSTOMER_SEARCH_PAGE_SIZE,
          q: normalizedEmail,
          sortBy: 'name',
          sortDir: 'asc'
        });
        if (!isActive) return;
        const exact = (data.items || []).find(
          (c) => normalizeSearchText(c.email) === normalizedEmail
        );
        if (exact) handleSelectExistingCustomer(exact);
      } catch {
        /* Ignore lookup errors here */
      }
    };
    lookup();
    return () => {
      isActive = false;
    };
  }, [
    customerDetails.email,
    customers,
    customerSearchResults,
    selectedCustomerId,
    handleSelectExistingCustomer
  ]);

  useEffect(() => {
    const term = companySearch.trim();
    if (!term) {
      setCompanySearchResults((prev) => {
        const selected = prev.find((c) => c.id === selectedCompanyId);
        return selected ? [selected] : [];
      });
      setCompanySearchError('');
      setIsCompanySearchLoading(false);
      return;
    }

    let isActive = true;
    setIsCompanySearchLoading(true);
    setCompanySearchError('');
    setCompanySearchResults([]);

    const timeoutId = window.setTimeout(() => {
      if (!isActive) return;
      api
        .listStaffCompanies({
          page: 1,
          pageSize: 50,
          q: term,
          status: 'active,on_hold',
          sortBy: 'name',
          sortDir: 'asc'
        })
        .then((data) => {
          if (isActive) setCompanySearchResults(data.items || []);
        })
        .catch((err: unknown) => {
          if (isActive) {
            setCompanySearchResults([]);
            setCompanySearchError(normalizeErrorMessage(err, 'Failed to search companies.'));
          }
        })
        .finally(() => {
          if (isActive) setIsCompanySearchLoading(false);
        });
    }, 250);

    return () => {
      isActive = false;
      window.clearTimeout(timeoutId);
    };
  }, [companySearch, selectedCompanyId]);

  // The "Booked By" list is always the selected company's own account holders.
  // Refetched per company rather than read from a cached list: staff move
  // between companies inside a single booking, and a stale team would offer a
  // member the server would then reject as belonging elsewhere.
  useEffect(() => {
    setBookedByCompanyUserId('primary');
    if (!isCorporate || !selectedCompanyId) {
      setCompanyTeam([]);
      setIsCompanyTeamLoading(false);
      return;
    }

    let isActive = true;
    setIsCompanyTeamLoading(true);
    api
      .listCompanyTeam(selectedCompanyId, { page: 1, pageSize: 200 })
      .then((data) => {
        if (isActive) setCompanyTeam(data.items || []);
      })
      .catch(() => {
        // A failed team fetch must not block the booking: the picker falls back
        // to the company's main contact, which is who the copy went to before.
        if (isActive) setCompanyTeam([]);
      })
      .finally(() => {
        if (isActive) setIsCompanyTeamLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [isCorporate, selectedCompanyId]);

  const bookedByOptions = useMemo(
    () => (isCorporate ? buildBookedByOptions(selectedCompany, companyTeam) : []),
    [isCorporate, selectedCompany, companyTeam]
  );

  const bookedByForPayload =
    isCorporate && selectedCompanyId && bookedByOptions.some((o) => o.id === bookedByCompanyUserId)
      ? bookedByCompanyUserId
      : undefined;

  const getPrice = useCallback(
    (item: Service | AddOn) =>
      getTierPrice(item, { company: selectedCompany || null, isCorporate }),
    [isCorporate, selectedCompany]
  );

  const selectedServices = useMemo(
    () =>
      bookingData.serviceIds
        .map((id) => servicesById.get(id))
        .filter((s): s is Service => Boolean(s)),
    [bookingData.serviceIds, servicesById]
  );
  const requiresAddOnSelection = selectedServices.some(
    (service) =>
      Number(getPrice(service)) <= 0 && service.name.toLowerCase().includes('health surveillance')
  );
  const availableAddOns = useMemo(
    () =>
      bookingData.serviceIds.length > 0
        ? addOns.filter((a) =>
            a.compatibleServiceIds.some((id) => bookingData.serviceIds.includes(id))
          )
        : [],
    [addOns, bookingData.serviceIds]
  );
  const compatibleAddOnIds = useMemo(
    () => new Set(availableAddOns.map((a) => a.id)),
    [availableAddOns]
  );
  const hasCompatibleAddOn = bookingData.addOnIds.some((id) => compatibleAddOnIds.has(id));
  const isAddOnSelectionMissing = requiresAddOnSelection && !hasCompatibleAddOn;

  const getTotalDuration = () => {
    let duration = selectedServices.reduce((sum, s) => sum + (s.durationMinutes || 0), 0);
    if (duration <= 0) duration = 30;
    bookingData.addOnIds.forEach((id) => {
      const a = addOnsById.get(id);
      if (a) duration += a.durationMinutes || 0;
    });
    return duration;
  };

  const selectedVenue = bookingData.venueId ? venuesById.get(bookingData.venueId) : undefined;
  const requiredDuration = getTotalDuration();

  const now = new Date();
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const minBookableDate = new Date(today);
  const monthStart = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), 1);
  const minMonthStart = new Date(minBookableDate.getFullYear(), minBookableDate.getMonth(), 1);
  const canGoPrevMonth = monthStart.getTime() > minMonthStart.getTime();

  const handleMonthChange = (offset: number) => {
    const nextMonth = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + offset, 1);
    if (offset < 0 && nextMonth.getTime() < minMonthStart.getTime()) return;
    setVisibleMonth(nextMonth);
    setBookingData((prev) => {
      if (!prev.date) return prev;
      const inNextMonth =
        prev.date.getFullYear() === nextMonth.getFullYear() &&
        prev.date.getMonth() === nextMonth.getMonth();
      return inNextMonth ? prev : { ...prev, date: null, time: '' };
    });
  };

  const getPricingForCheckout = useCallback((): CheckoutPricing => {
    if (checkoutPricing) return checkoutPricing;
    let subtotalAmount = 0;
    selectedServices.forEach((service) => {
      subtotalAmount += getPrice(service);
    });
    bookingData.addOnIds.forEach((id) => {
      const a = addOnsById.get(id);
      if (a) subtotalAmount += getPrice(a);
    });
    return {
      currency: 'GBP',
      subtotalAmount,
      vatRatePercent: 0,
      vatAmount: 0,
      totalAmount: subtotalAmount
    };
  }, [checkoutPricing, selectedServices, bookingData.addOnIds, addOnsById, getPrice]);

  const finalizeBooking = useCallback(
    async (overrides?: { paymentId?: string; paymentLinkToken?: string }) => {
      const pricing = getPricingForCheckout();
      const phoneValue = customerDetails.phone.trim();
      const phoneResult = phoneValue ? validateUkPhone(phoneValue) : null;
      const hasQuestionnaire =
        selectedServices.some((service) => service.questionnaireConfig?.templateId) ||
        bookingData.addOnIds.some((id) => addOnsById.get(id)?.questionnaireConfig?.templateId);
      const resolvedPaymentId = overrides?.paymentId ?? pendingPaymentId;
      const resolvedPaymentLinkToken = overrides?.paymentLinkToken ?? pendingPaymentLinkToken;
      const appointment: Appointment = {
        id: '',
        venueId: bookingData.venueId,
        serviceId: bookingData.serviceIds[0] || '',
        serviceIds: bookingData.serviceIds,
        addOnIds: bookingData.addOnIds,
        // Local calendar day, not UTC: .toISOString() rolls the day back one for
        // UK/negative-offset users, storing the booking on the day before selected.
        date: formatDateOnly(bookingData.date!),
        time: bookingData.time,
        customerName: customerDetails.name,
        customerEmail: customerDetails.email,
        customerPhone: phoneResult && phoneResult.valid ? phoneResult.normalized : '',
        customerDob: normalizeDobToIso(customerDetails.dob),
        customerJobTitle: customerDetails.jobTitle,
        allowOverbook: allowOverbook || undefined,
        companyId: isCorporate ? selectedCompanyId : undefined,
        bookedByCompanyUserId: bookedByForPayload,
        poNumber: isCorporate ? poNumber : undefined,
        paymentMethod: paymentMethod,
        paymentId: paymentMethod === 'card' ? resolvedPaymentId : undefined,
        paymentLinkToken: paymentMethod === 'card' ? resolvedPaymentLinkToken : undefined,
        subtotalAmount: pricing.subtotalAmount,
        vatRatePercent: pricing.vatRatePercent,
        vatAmount: pricing.vatAmount,
        totalAmount: pricing.totalAmount,
        currency: pricing.currency,
        status: 'booked',
        createdAt: new Date().toISOString(),
        questionnaireStatus: hasQuestionnaire ? 'pending' : 'not_required'
      };
      const didCharge = paymentMethod === 'card' && Boolean(resolvedPaymentId);
      try {
        await onSave(appointment);
      } catch (err: unknown) {
        const message = normalizeErrorMessage(
          err,
          didCharge
            ? 'Payment was taken but the booking could not be created. Click "Process Transaction" to retry the booking — the card will not be charged again.'
            : 'The booking could not be created. Please try again.'
        );
        setError(message);
        setPaymentResultModal({ type: 'error', message });
        setIsProcessing(false);
        return;
      }
      setPendingPaymentId(undefined);
      setPendingPaymentLinkToken(undefined);
      setIsProcessing(false);
      setPaymentResultModal({
        type: 'success',
        title: didCharge ? 'Payment Successful' : 'Booking Confirmed',
        message: didCharge
          ? 'Payment completed and the booking is confirmed.'
          : 'The booking is confirmed.'
      });
    },
    [
      bookingData,
      customerDetails,
      isCorporate,
      selectedCompanyId,
      bookedByForPayload,
      paymentMethod,
      pendingPaymentId,
      pendingPaymentLinkToken,
      poNumber,
      allowOverbook,
      getPricingForCheckout,
      servicesById,
      addOnsById,
      onSave
    ]
  );

  useEffect(() => {
    if (bookingData.serviceIds.length === 0) {
      setCheckoutPricing(null);
      return;
    }
    let isCurrent = true;
    api
      .getCheckoutQuote({
        serviceId: bookingData.serviceIds[0],
        serviceIds: bookingData.serviceIds,
        addOnIds: bookingData.addOnIds,
        companyId: isCorporate ? selectedCompanyId : undefined,
        currency: 'GBP'
      })
      .then((r) => {
        if (isCurrent) setCheckoutPricing(r.pricing);
      })
      .catch(() => {
        if (isCurrent) setCheckoutPricing(null);
      });
    return () => {
      isCurrent = false;
    };
  }, [bookingData.serviceIds, bookingData.addOnIds, isCorporate, selectedCompanyId]);

  useEffect(() => {
    setPendingPaymentId(undefined);
    setPendingPaymentLinkToken(undefined);
  }, [
    bookingData.serviceIds,
    bookingData.addOnIds,
    bookingData.venueId,
    bookingData.date,
    bookingData.time,
    isCorporate,
    selectedCompanyId,
    selectedCustomerId
  ]);

  useEffect(() => {
    if (!bookingData.date || !bookingData.venueId || bookingData.serviceIds.length === 0) {
      setAvailabilitySlots([]);
      setHasAvailabilityData(false);
      setIsLoadingAvailability(false);
      return;
    }

    let isCurrent = true;
    setIsLoadingAvailability(true);
    setHasAvailabilityData(false);

    api
      .getBookingAvailability({
        venueId: bookingData.venueId,
        date: formatDateOnly(bookingData.date),
        serviceId: bookingData.serviceIds[0],
        serviceIds: bookingData.serviceIds,
        addOnIds: bookingData.addOnIds
      })
      .then((result) => {
        if (!isCurrent) return;
        const slots = filterPastAvailabilitySlots(bookingData.date, result?.slots || []);
        setAvailabilitySlots(slots);
        setHasAvailabilityData(true);
        setBookingData((prev) => {
          if (!prev.time) return prev;
          const selectedSlot = slots.find((s) => s.time === prev.time);
          if (!selectedSlot) return { ...prev, time: '' };
          if (selectedSlot.available || allowOverbook) return prev;
          return { ...prev, time: '' };
        });
      })
      .catch(() => {
        if (!isCurrent) return;
        setAvailabilitySlots([]);
        setHasAvailabilityData(false);
      })
      .finally(() => {
        if (isCurrent) setIsLoadingAvailability(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [
    bookingData.date,
    bookingData.venueId,
    bookingData.serviceIds,
    bookingData.addOnIds,
    allowOverbook
  ]);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (!event?.data || event.data.type !== 'opayo:3ds') return;
      setThreeDSData(null);
      if (!event.data.success) {
        const message = event.data.statusDetail || '3DS verification failed.';
        setError(message);
        setIsProcessing(false);
        setPaymentResultModal({ type: 'error', message });
        return;
      }
      const freshPaymentId: string | undefined = event.data.paymentId || undefined;
      const freshPaymentLinkToken: string | undefined = event.data.paymentLinkToken || undefined;
      if (freshPaymentId) setPendingPaymentId(freshPaymentId);
      if (freshPaymentLinkToken) setPendingPaymentLinkToken(freshPaymentLinkToken);
      void finalizeBooking({ paymentId: freshPaymentId, paymentLinkToken: freshPaymentLinkToken });
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [finalizeBooking]);

  useEffect(() => {
    if (!threeDSData) return;
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = threeDSData.acsUrl;
    form.target = 'opayo-3ds-iframe';
    const addField = (name: string, value: string) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    };
    addField('creq', threeDSData.cReq);
    addField('TermUrl', threeDSData.termUrl);
    if (threeDSData.threeDSSessionData)
      addField('threeDSSessionData', threeDSData.threeDSSessionData);
    document.body.appendChild(form);
    form.submit();
    return () => {
      document.body.removeChild(form);
    };
  }, [threeDSData]);

  const dobValue = customerDetails.dob.trim();
  const isDobValid = validateDob(dobValue).valid;
  const showDobError = dobTouched && !isDobValid;

  const emailValue = customerDetails.email.trim();
  const isEmailValid = validateEmail(emailValue).valid;
  const showEmailError = emailTouched && !isEmailValid;

  const phoneValue = customerDetails.phone.trim();
  const phoneResult = phoneValue ? validateUkPhone(phoneValue) : null;
  const isPhoneValid = !phoneResult || phoneResult.valid;
  const showPhoneError = phoneTouched && !isPhoneValid;

  const canContinueToBooking = Boolean(
    bookingData.venueId &&
    bookingData.serviceIds.length > 0 &&
    bookingData.date &&
    bookingData.time &&
    !isAddOnSelectionMissing
  );
  const canSubmit = Boolean(
    bookingData.venueId &&
    bookingData.serviceIds.length > 0 &&
    bookingData.date &&
    bookingData.time &&
    customerDetails.name &&
    customerDetails.email &&
    customerDetails.dob &&
    (isCorporate ? selectedCompanyId : true) &&
    !isAddOnSelectionMissing
  );

  const pricing = getPricingForCheckout();

  const handleSubmit = async () => {
    // Guard against double-submit: a second click while the first is in flight
    // would otherwise re-charge the card / create a duplicate booking.
    if (isProcessing) return;
    setError('');
    if (isAddOnSelectionMissing) {
      setError('Please select at least one add-on for Health Surveillance bookings.');
      return;
    }
    const cleanCardNumber = cardDetails.number.replace(/\D/g, '');
    const cleanCvc = cardDetails.cvc.replace(/\D/g, '');
    if (!customerDetails.name.trim() || !customerDetails.email.trim()) {
      setError('Please enter the patient name and email.');
      return;
    }
    if (customerDetails.name.trim().split(/\s+/).filter(Boolean).length < 2) {
      setError('Please enter both a first name and a surname.');
      return;
    }
    if (!isDobValid) {
      setDobTouched(true);
      setError(
        `Please enter a valid date of birth in DD-MM-YYYY format (year 1900-${currentYear}).`
      );
      return;
    }
    if (!isEmailValid) {
      setEmailTouched(true);
      setError('Please enter a valid email address.');
      return;
    }
    if (!isPhoneValid) {
      setPhoneTouched(true);
      setError('Please enter a valid UK phone number.');
      return;
    }
    if (isCorporate && selectedCompany?.requirePo && !poNumber.trim()) {
      setError(`Company "${selectedCompany.name}" requires a PO Number for all bookings.`);
      return;
    }

    if (paymentMethod === 'card') {
      if (!cardDetails.cardholderName.trim()) {
        setError('Please enter the cardholder name.');
        return;
      }
      if (
        cleanCardNumber.length < 12 ||
        cleanCardNumber.length > 19 ||
        cardDetails.expiry.length < 5 ||
        cleanCvc.length < 3 ||
        cleanCvc.length > 4
      ) {
        setError('Please enter valid credit card details.');
        return;
      }
      if (
        !billingAddress.address1.trim() ||
        !billingAddress.city.trim() ||
        !billingAddress.postalCode.trim() ||
        !billingAddress.country.trim()
      ) {
        setError('Please enter billing address details.');
        return;
      }
      if (!/^[A-Za-z]{2}$/.test(billingAddress.country.trim())) {
        setError('Please select a valid billing country.');
        return;
      }
    }

    if (!bookingData.date) return;

    if (paymentMethod === 'card' && pendingPaymentId) {
      setIsProcessing(true);
      await finalizeBooking({
        paymentId: pendingPaymentId,
        paymentLinkToken: pendingPaymentLinkToken
      });
      return;
    }

    if (paymentMethod === 'card') {
      setPendingPaymentId(undefined);
      setPendingPaymentLinkToken(undefined);
      setIsProcessing(true);
      if (bookingData.serviceIds.length === 0) {
        setError('Please select a service.');
        setIsProcessing(false);
        return;
      }
      try {
        const quote = await api.getCheckoutQuote({
          serviceId: bookingData.serviceIds[0],
          serviceIds: bookingData.serviceIds,
          addOnIds: bookingData.addOnIds,
          companyId: isCorporate ? selectedCompanyId : undefined,
          currency: 'GBP'
        });
        setCheckoutPricing(quote.pricing);
        const totalAmount = quote.pricing.totalAmount;
        if (totalAmount > 0) {
          const expiryDigits = cardDetails.expiry.replace(/\D/g, '').slice(0, 4);
          const nameParts = customerDetails.name.trim().split(/\s+/).filter(Boolean);
          const firstName = nameParts[0] || 'Customer';
          const lastName = nameParts.slice(1).join(' ') || 'Customer';
          const session = await api.createOpayoSessionKey();
          const cardIdentifier = await tokenizeCardDetails({
            session,
            cardDetails: {
              number: cleanCardNumber,
              expiry: expiryDigits,
              cvc: cardDetails.cvc,
              cardholderName:
                cardDetails.cardholderName.trim() || customerDetails.name.trim() || 'Customer'
            }
          });

          const paymentResult = await api.processOpayoPayment({
            amount: totalAmount,
            expectedTotal: totalAmount,
            currency: 'GBP',
            description: `Manual Booking - ${
              selectedServices.map((s) => s.name).join(' + ') || 'Appointment'
            }`,
            merchantSessionKey: session.merchantSessionKey,
            cardIdentifier,
            billingAddress: {
              address1: billingAddress.address1.trim(),
              city: billingAddress.city.trim(),
              postalCode: billingAddress.postalCode.trim(),
              country: billingAddress.country.trim().toUpperCase()
            },
            customer: {
              firstName,
              lastName,
              email: customerDetails.email.trim(),
              phone: phoneResult && phoneResult.valid ? phoneResult.normalized : ''
            },
            browser: buildBrowserInfo(),
            serviceId: bookingData.serviceIds[0],
            serviceIds: bookingData.serviceIds,
            addOnIds: bookingData.addOnIds,
            companyId: isCorporate ? selectedCompanyId : undefined,
            // Also on the charge, not just the booking below: if the follow-up
            // POST /bookings is lost, the server rebuilds the booking from this
            // metadata and must attribute it to the same person.
            bookedByCompanyUserId: bookedByForPayload
          });

          const freshPaymentId: string | undefined = paymentResult?.paymentId || undefined;
          const freshPaymentLinkToken: string | undefined =
            paymentResult?.paymentLinkToken || undefined;
          if (freshPaymentId) setPendingPaymentId(freshPaymentId);
          if (freshPaymentLinkToken) setPendingPaymentLinkToken(freshPaymentLinkToken);

          if (
            paymentResult?.requires3DS &&
            paymentResult.acsUrl &&
            paymentResult.cReq &&
            paymentResult.termUrl
          ) {
            setThreeDSData({
              acsUrl: paymentResult.acsUrl,
              cReq: paymentResult.cReq,
              termUrl: paymentResult.termUrl,
              threeDSSessionData: paymentResult.threeDSSessionData
            });
            return;
          }
          await finalizeBooking({
            paymentId: freshPaymentId,
            paymentLinkToken: freshPaymentLinkToken
          });
          return;
        }
      } catch (err: unknown) {
        const message = normalizeErrorMessage(err, 'Payment failed. Please try again.');
        setError(message);
        setPaymentResultModal({ type: 'error', message });
        setIsProcessing(false);
        return;
      }
    }
    setIsProcessing(true);
    await finalizeBooking();
  };

  const closePaymentResultModal = () => {
    const wasSuccess = paymentResultModal?.type === 'success';
    setPaymentResultModal(null);
    if (wasSuccess) onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4 overflow-y-auto">
      {paymentResultModal && (
        <div className="fixed inset-0 z-[90] bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 text-center border border-slate-200">
            <div
              className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 ${paymentResultModal.type === 'success' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}
            >
              {paymentResultModal.type === 'success' ? (
                <CheckCircle className="w-8 h-8" />
              ) : (
                <AlertCircle className="w-8 h-8" />
              )}
            </div>
            <h4 className="text-lg font-bold text-slate-900 mb-2">
              {paymentResultModal.title ??
                (paymentResultModal.type === 'success' ? 'Payment Successful' : 'Payment Failed')}
            </h4>
            <p className="text-slate-600 text-sm mb-6">{paymentResultModal.message}</p>
            <button
              onClick={closePaymentResultModal}
              className={`w-full py-2.5 rounded-xl text-sm font-bold text-white ${paymentResultModal.type === 'success' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
            >
              {paymentResultModal.type === 'success' ? 'Continue' : 'Close'}
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden animate-fade-in flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="p-6 border-b bg-slate-50 flex justify-between items-center">
          <div>
            <h3 className="text-xl font-bold text-slate-800">New Manual Booking</h3>
            <p className="text-xs text-slate-500">
              Creating a record from phone or offline request
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-200 rounded-full text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {step === 1 ? (
            <BookingScheduleStep
              venues={venues}
              services={services}
              addOns={addOns}
              availableAddOns={availableAddOns}
              bookingData={bookingData}
              onBookingDataChange={setBookingData}
              getPrice={getPrice}
              requiresAddOnSelection={requiresAddOnSelection}
              isAddOnSelectionMissing={isAddOnSelectionMissing}
              selectedVenue={selectedVenue}
              visibleMonth={visibleMonth}
              onMonthChange={handleMonthChange}
              canGoPrevMonth={canGoPrevMonth}
              availabilitySlots={availabilitySlots}
              hasAvailabilityData={hasAvailabilityData}
              isLoadingAvailability={isLoadingAvailability}
              allowOverbook={allowOverbook}
              onAllowOverbookChange={setAllowOverbook}
              requiredDuration={requiredDuration}
              appointments={appointments}
              servicesById={servicesById}
              addOnsById={addOnsById}
            />
          ) : (
            <BookingPatientStep
              customerSearch={customerSearch}
              onCustomerSearchChange={setCustomerSearch}
              filteredCustomers={filteredCustomers}
              isCustomerSearchLoading={isCustomerSearchLoading}
              customerSearchError={customerSearchError}
              selectedCustomerId={selectedCustomerId}
              onSelectExistingCustomer={handleSelectExistingCustomer}
              onCreateNewCustomer={handleCreateNewCustomer}
              customerDetails={customerDetails}
              onCustomerDetailsChange={setCustomerDetails}
              dobTouched={dobTouched}
              onDobTouched={() => setDobTouched(true)}
              emailTouched={emailTouched}
              onEmailTouched={() => setEmailTouched(true)}
              phoneTouched={phoneTouched}
              onPhoneTouched={() => setPhoneTouched(true)}
              showDobError={showDobError}
              showEmailError={showEmailError}
              showPhoneError={showPhoneError}
              currentYear={currentYear}
              isCorporate={isCorporate}
              onIsCorporateChange={setIsCorporate}
              paymentMethod={paymentMethod}
              onPaymentMethodChange={setPaymentMethod}
              companySearch={companySearch}
              onCompanySearchChange={setCompanySearch}
              isCompanyDropdownOpen={isCompanyDropdownOpen}
              onCompanyDropdownOpen={() => setIsCompanyDropdownOpen(true)}
              onCompanyDropdownClose={() => setIsCompanyDropdownOpen(false)}
              filteredCompanies={filteredCompanies}
              isCompanySearchLoading={isCompanySearchLoading}
              companySearchError={companySearchError}
              selectedCompanyId={selectedCompanyId}
              selectedCompany={selectedCompany}
              onSelectCompany={(id, name) => {
                setSelectedCompanyId(id);
                setCompanySearch(name);
                setIsCompanyDropdownOpen(false);
              }}
              poNumber={poNumber}
              onPoNumberChange={setPoNumber}
              bookedByOptions={bookedByOptions}
              bookedByCompanyUserId={bookedByCompanyUserId}
              onBookedByChange={setBookedByCompanyUserId}
              isCompanyTeamLoading={isCompanyTeamLoading}
              cardDetails={cardDetails}
              onCardDetailsChange={setCardDetails}
              billingAddress={billingAddress}
              onBillingAddressChange={setBillingAddress}
              pricing={pricing}
              hasService={bookingData.serviceIds.length > 0}
            />
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-slate-50 flex justify-between items-center">
          <div className="flex-1">
            {error && (
              <p className="text-xs font-bold text-red-600 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> {error}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            {step === 2 && (
              <button
                onClick={() => setStep(1)}
                disabled={isProcessing}
                className="px-6 py-2 border border-slate-300 text-slate-700 rounded-lg font-bold hover:bg-white flex items-center gap-2 text-sm disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" /> Back to Schedule
              </button>
            )}
            {step === 1 ? (
              <button
                onClick={() => setStep(2)}
                disabled={!canContinueToBooking}
                className="bg-slate-900 text-white px-8 py-2 rounded-lg font-bold hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm"
              >
                Patient Details →
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={!canSubmit || isProcessing}
                className={`px-10 py-2 rounded-lg font-bold transition-all text-sm flex items-center gap-2 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed ${paymentMethod === 'card' ? 'bg-purple-600 hover:bg-purple-700 text-white' : 'bg-teal-600 hover:bg-teal-700 text-white'}`}
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Authorizing with Opayo...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4" />
                    {paymentMethod === 'card' ? 'Process Transaction' : 'Complete Booking'}
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {threeDSData && (
          <div className="fixed inset-0 z-[70] bg-slate-900/70 flex items-center justify-center p-4">
            <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-bold text-slate-800">3D Secure Verification</h4>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-400">Complete the challenge to continue</span>
                  {/* Escape hatch: the 3DS overlay covers the header X, so without
                      this the operator is trapped if the bank challenge hangs or
                      is abandoned (no opayo:3ds message ever arrives). */}
                  <button
                    onClick={() => {
                      setThreeDSData(null);
                      setIsProcessing(false);
                      setPendingPaymentId(undefined);
                      setPendingPaymentLinkToken(undefined);
                      setError(
                        'Card verification was cancelled. No booking was created. If your card was charged, check the bookings list before retrying.'
                      );
                    }}
                    className="text-xs font-semibold text-red-600 hover:text-red-700 px-2 py-1 rounded-lg hover:bg-red-50"
                  >
                    Cancel verification
                  </button>
                </div>
              </div>
              <iframe
                title="3DS Verification"
                name="opayo-3ds-iframe"
                className="w-full h-[70vh] border rounded-xl"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
